import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const useShiftSwapSettings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['shift-swap-settings'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('shift_swap_settings')
        .select('*')
        .eq('admin_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (isEnabled: boolean) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('shift_swap_settings')
        .upsert({
          admin_id: user.id,
          is_enabled: isEnabled,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'admin_id'
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-swap-settings'] });
      toast({
        title: 'Settings updated',
        description: 'Shift swap feature has been updated successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to update shift swap settings.',
        variant: 'destructive',
      });
      console.error('Error updating shift swap settings:', error);
    },
  });

  return {
    isEnabled: settings?.is_enabled ?? false,
    isLoading,
    toggleSwapFeature: toggleMutation.mutate,
  };
};
